import { h } from "preact";
import { useCallback, useEffect, useMemo, useState } from "preact/hooks";
import {
    checkIfCanPrint,
    generate,
    labelPreviewUrl,
    labelsMetadata,
    LabelTemplateFields,
    PrinterInfo as PrinterInfoType,
    slaveInfo,
    slavePrint,
} from "../api/label-creator";
import { egzotechAgentManager } from "../App";
import { useCommandHandler } from "../hooks";
import { Command } from "../hooks/Command";
import { Printing } from "./Printing";
import { PrinterInfo } from "./PrinterInfo";
import { TemplateQuickInfo } from "./TemplateQuickInfo";
import { distinct } from "../helpers/distinct";
import { cn } from "../helpers/cn";

const tapeType: { [key: string]: string | undefined } = {
    "1": "laminated, strong adhesive, security laminated, flexible ID tape",
    "3": "security, heat shrink",
    "4": "fabric tape",
    "20": "flexible id, starry laminated",
};

const printErrors = {
    "invalid-tape-width": "Szerokość włożonej taśmy nie zgadza się z żadną z szerokości naklejek",
    "no-printer": "Brak komunikacji z drukarką",
    "no-template": "Brak wybranego szablonu",
};

export function parseTapeType(tapeWidth?: string, tapeType?: string) {
    const tapeWidthNumber = parseInt(tapeWidth ?? "", 10);

    if (!tapeType || isNaN(tapeWidthNumber) || !isFinite(tapeWidthNumber) || tapeWidthNumber < 0) {
        return null;
    }

    const tapeTypeRegex = /(.*)ink\s+on(.*)tape.*?([a-zA-Z0-9 ]+)/gm;
    const result = tapeTypeRegex.exec(tapeType);

    if (!result) {
        return null;
    }

    return `${tapeWidthNumber} ${result[1].trim().replace(/\s+/gm, "_")}_on_${result[2].trim().replace(/\s+/gm, "_")} ${result[3].trim().replace(/\s+/gm, "_")}.jpg`;
}

export function TemplateMultipartPrint(props: {
    templates: string[];
    fields: LabelTemplateFields[];
    overrideFields?: { [key: string]: string }[];
    uniqueCopies?: number;
    copies?: number;
    noAutoCut?: boolean;
    tapeImgBase?: string;
    onPrint?: () => void;
    onProvideFields?: (
        copy: number,
        index: number,
        cache: Record<string, unknown>,
    ) =>
        | { fields: LabelTemplateFields; overrideFields?: { [key: string]: string } }
        | Promise<{ fields: LabelTemplateFields; overrideFields?: { [key: string]: string } }>;
}) {
    const [printerInfo, setPrinterInfo] = useState(null as PrinterInfoType | null);
    const [fetchedTemplateMetadatas, setTemplateMetadatas] = useState(null as null | { [key: string]: string }[]);
    const [forceAllow, setForceAllow] = useState(false);

    useEffect(() => {
        Promise.all(
            props.templates.map(template => labelsMetadata(template) as Promise<{ [key: string]: string }>),
        ).then(setTemplateMetadatas);
    }, [props.templates]);

    const templateMetadatas = useMemo(
        () =>
            fetchedTemplateMetadatas
                ? fetchedTemplateMetadatas.map((metadata, i) => ({
                      ...metadata,
                      ...props.overrideFields?.[i],
                  }))
                : null,
        [fetchedTemplateMetadatas, props.overrideFields],
    );

    const tapeTypeUrls = templateMetadatas
        ? templateMetadatas.map(metadata => parseTapeType(metadata["tape-width"], metadata["tape-type"]))
        : null;
    const tapeImgs = distinct(tapeTypeUrls ? tapeTypeUrls.map(url => `${props.tapeImgBase}${url}`) : []);

    const allowPrint =
        templateMetadatas && printerInfo
            ? ((forceAllow ||
                  templateMetadatas
                      .map(v => checkIfCanPrint(v, printerInfo))
                      .sort((a, b) => (typeof a === "boolean" ? -1 : 0))?.[0]) ??
                  true) ||
              true
            : false;

    const printCallback = useCallback(
        async (uniqueCopies: number, noAutoCut: boolean) => {
            if (props.onProvideFields) {
                for (let i = 0; i < uniqueCopies; i++) {
                    const cache = {} as Record<string, unknown>;

                    for (let j = 0; j < props.templates.length; j++) {
                        if (checkIfCanPrint(templateMetadatas?.[j] ?? {}, printerInfo!) !== true && !forceAllow) {
                            continue;
                        }

                        let fieldsData = props.onProvideFields(i, j, cache);

                        if ("then" in fieldsData) {
                            fieldsData = await fieldsData;
                        }

                        const copies = props.copies ?? 1;

                        for (let k = 0; k < copies; k++) {
                            await slavePrint(
                                egzotechAgentManager.getAgentWithModule("label-printer")!,
                                props.templates[j],
                                fieldsData.fields,
                                {
                                    copies:
                                        i === uniqueCopies - 1 && k === copies - 1 && j === props.templates.length - 1
                                            ? 1
                                            : i === 0 && k === 0 && j === 0
                                              ? -2
                                              : -1,
                                    noAutoCut,
                                },
                                fieldsData.overrideFields,
                            );
                        }
                    }
                }
            } else {
                for (let j = 0; j < props.templates.length; j++) {
                    if (checkIfCanPrint(templateMetadatas?.[j] ?? {}, printerInfo!) !== true && !forceAllow) {
                        continue;
                    }

                    await slavePrint(
                        egzotechAgentManager.getAgentWithModule("label-printer")!,
                        props.templates[j],
                        props.fields[j],
                        { copies: uniqueCopies, noAutoCut },
                        props.overrideFields?.[j],
                    );
                }
            }

            if (props.onPrint) {
                props.onPrint();
            }
        },
        [
            props.onProvideFields,
            props.onPrint,
            props.templates,
            props.fields,
            props.overrideFields,
            templateMetadatas,
            printerInfo,
        ],
    );

    return (
        <div class="template-print">
            <div class="printer-template-preview">
                <div class="printer-template">
                    <div class="paper-2 printer-info">
                        <h2>Informacje o drukarce</h2>
                        <PrinterInfo onChange={info => setPrinterInfo(info)} />
                    </div>
                    <div class="paper-2 template-info">
                        <h2>Informacje o szablonie</h2>
                        {templateMetadatas?.map((metadata, index) => (
                            <TemplateQuickInfo key={index} templateMetadata={metadata} fields={props.fields?.[index]} />
                        ))}
                    </div>
                </div>
                <div class="preview paper-2">
                    <h2>Podgląd</h2>
                    <div class="flex flex-col gap-2 items-start">
                        {props.templates.map((template, index) => (
                            <div
                                class="relative"
                                title={
                                    printerInfo &&
                                    checkIfCanPrint(templateMetadatas?.[index] ?? {}, printerInfo) ===
                                        "invalid-tape-width"
                                        ? "Naklejka nie zostanie wydrukowana, niezgodna szerokość taśmy"
                                        : ""
                                }
                            >
                                <img
                                    key={index}
                                    src={generate(template, props.fields?.[index], props.overrideFields?.[index])}
                                />
                                <div
                                    class={cn(
                                        "absolute top-0 left-0 w-full h-full bg-gray-700/75 flex flex-col gap-2",
                                        "justify-center items-center text-center p-2 font-bold z-10 text-xs",
                                        printerInfo &&
                                            checkIfCanPrint(templateMetadatas?.[index] ?? {}, printerInfo) ===
                                                "invalid-tape-width" &&
                                            !forceAllow
                                            ? "visible"
                                            : "hidden",
                                    )}
                                >
                                    <div class="line-clamp-3 overflow-hidden">
                                        Naklejka nie zostanie wydrukowana, niezgodna szerokość taśmy
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
            <div class="print-tape-img">
                <div class="print paper-2" style="margin-top: 10px;">
                    <h2
                        onClick={e => {
                            if (e.detail === 3) {
                                setForceAllow(!forceAllow);
                            }
                        }}
                    >
                        Akcje
                    </h2>
                    <Printing
                        allowPrint={allowPrint === true}
                        copies={props.uniqueCopies}
                        noAutoCut={props.noAutoCut}
                        onPrint={printCallback}
                    />
                    {typeof allowPrint === "string" ? (
                        <div>
                            <div class="alert error">{printErrors[allowPrint]}</div>
                        </div>
                    ) : null}
                </div>
                {tapeImgs ? (
                    <div class="tape-img paper-2">
                        <h2>Rodzaj taśmy</h2>
                        <div class="preview flex flex-row gap-2">
                            {tapeImgs.map((tapeImg, index) => (
                                <img key={index} src={tapeImg} />
                            ))}
                        </div>
                    </div>
                ) : null}
            </div>
        </div>
    );
}
